import React from "react";
import { api, ASSET_URL } from "../../lib/api";
import { Upload, Sparkles, Trash2, Save, Film, Code2, ExternalLink, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import PdfViewer from "../../components/PdfViewer";

export default function MediaLibrary() {
  const [items, setItems] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [viewingPdf, setViewingPdf] = React.useState(null);
  const fileRef = React.useRef();

  const load = () => api.adminMedia().then(d => {
    setItems(d);
    setSelected(s => (s ? (d.find(x => x.id === s.id) || s) : s));
  });
  React.useEffect(() => { load(); }, []);

  const handleUpload = async (e, type = "image") => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        await api.uploadMedia(f, type);
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
      load();
      // Alt-text + title are refined by the AI in the background — reload shortly to show them.
      if (type === "image") {
        toast.message("KI generiert Alt-Texte …", { description: "Titel & Alt-Text erscheinen in wenigen Sekunden automatisch." });
        setTimeout(() => load(), 7000);
        setTimeout(() => load(), 15000);
      }
    } catch (err) {
      toast.error("Upload fehlgeschlagen");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const genAlt = async (id) => {
    setBusy(true);
    try {
      const r = await api.genAlt(id);
      toast.success("Alt-Text generiert");
      setItems(its => its.map(x => x.id === id ? { ...x, alt_de: r.alt_de, alt_en: r.alt_en, title: r.title } : x));
      if (selected?.id === id) setSelected({ ...selected, alt_de: r.alt_de, alt_en: r.alt_en, title: r.title });
    } catch (e) { toast.error("KI-Alt-Text fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const saveMeta = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.updateMedia(selected.id, { alt_de: selected.alt_de, alt_en: selected.alt_en, title: selected.title, copyright: selected.copyright, description: selected.description });
      setItems(its => its.map(x => x.id === r.id ? r : x));
      toast.success("Gespeichert");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const item = items.find(entry => entry.id === id);
    const label = item?.original_filename || "dieses Medium";
    if (!window.confirm(`„${label}“ wirklich dauerhaft löschen?\n\nWenn die Datei noch auf einer Seite verwendet wird, bleibt sie geschützt.`)) return;
    setBusy(true);
    try {
      await api.deleteMedia(id);
      setItems(its => its.filter(x => x.id !== id));
      setSelectedIds(ids => { const next = new Set(ids); next.delete(id); return next; });
      if (selected?.id === id) setSelected(null);
      toast.success("Medium vollständig gelöscht");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = detail?.message || (typeof detail === "string" ? detail : "Medium konnte nicht gelöscht werden");
      const usages = Array.isArray(detail?.usages) ? ` ${detail.usages.join(" · ")}` : "";
      toast.error(message, { description: usages || undefined, duration: 7000 });
    } finally { setBusy(false); }
  };

  const toggleSelected = (id) => {
    setSelectedIds(ids => {
      const next = new Set(ids);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedItems = items.filter(item => selectedIds.has(item.id));
  const selectedBytes = selectedItems.reduce((sum, item) => sum + (item.size_bytes || 0), 0);
  const formatBytes = (bytes) => bytes >= 1024 * 1024 * 1024
    ? `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
    : bytes >= 1024 * 1024
      ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
      : `${Math.round(bytes / 1024)} KB`;

  const removeSelected = async () => {
    if (!selectedItems.length) return;
    const names = selectedItems.slice(0, 5).map(item => `• ${item.original_filename}`).join("\n");
    const more = selectedItems.length > 5 ? `\n… und ${selectedItems.length - 5} weitere` : "";
    if (!window.confirm(`${selectedItems.length} Datei(en) mit insgesamt ${formatBytes(selectedBytes)} dauerhaft löschen?\n\n${names}${more}\n\nVerwendete Medien werden automatisch geschützt und nicht gelöscht.`)) return;

    setBusy(true);
    let deleted = 0;
    const blocked = [];
    try {
      for (const item of selectedItems) {
        try {
          await api.deleteMedia(item.id);
          deleted += 1;
        } catch (e) {
          const detail = e?.response?.data?.detail;
          blocked.push({ name: item.original_filename, message: detail?.message || "konnte nicht gelöscht werden", usages: detail?.usages || [] });
        }
      }
      await load();
      setSelectedIds(new Set());
      if (selectedIds.has(selected?.id) && !blocked.some(x => x.name === selected?.original_filename)) setSelected(null);
      if (deleted) toast.success(`${deleted} Datei(en) vollständig gelöscht`);
      if (blocked.length) {
        toast.error(`${blocked.length} verwendete Datei(en) wurden geschützt`, {
          description: blocked.slice(0, 3).map(x => `${x.name}: ${x.usages.join(", ") || x.message}`).join(" · "),
          duration: 9000,
        });
      }
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="brut-tag mb-3">CMS / MEDIATHEK</div>
          <h1 className="brut-h2">Medien</h1>
        </div>
        <div className="flex gap-2">
          <label className="brut-btn !py-2 !px-4 text-xs cursor-pointer" data-testid="upload-image-btn">
            <Upload size={12} /> Bilder hochladen
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => handleUpload(e, "image")} />
          </label>
          <label className="brut-btn brut-btn-ghost !py-2 !px-4 text-xs cursor-pointer">
            <Upload size={12} /> Video
            <input type="file" accept="video/mp4,video/webm" className="hidden" onChange={(e) => handleUpload(e, "video")} />
          </label>
          <label className="brut-btn brut-btn-ghost !py-2 !px-4 text-xs cursor-pointer">
            <Upload size={12} /> HTML-Animation
            <input type="file" accept=".html,.htm" className="hidden" onChange={(e) => handleUpload(e, "html_animation")} />
          </label>
          <label className="brut-btn brut-btn-ghost !py-2 !px-4 text-xs cursor-pointer" data-testid="upload-pdf-btn">
            <Upload size={12} /> PDF
            <input type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={(e) => handleUpload(e, "document")} />
          </label>
        </div>
      </div>

      <div className="mb-5 p-4 border-2 border-[var(--ink)] flex flex-wrap items-center justify-between gap-3" data-testid="media-selection-toolbar">
        <div>
          <div className="font-extrabold">Medien zum Löschen auswählen</div>
          <div className="text-xs opacity-70">Klicke die Checkbox an einer oder mehreren Kacheln. Verwendete Dateien bleiben geschützt.</div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-xs">{selectedItems.length} ausgewählt · {formatBytes(selectedBytes)}</span>
          {selectedItems.length > 0 && <button type="button" onClick={() => setSelectedIds(new Set())} className="text-xs font-bold underline">Auswahl aufheben</button>}
          <button type="button" onClick={removeSelected} disabled={busy || !selectedItems.length} className="border-2 border-red-600 bg-red-600 text-white px-4 py-2.5 font-mono text-xs uppercase disabled:opacity-40" data-testid="delete-selected-media">
            <Trash2 size={14}/> Ausgewählte löschen
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(m => (
            <div key={m.id} className="relative">
            <button onClick={() => setSelected(m)} className={`w-full border-2 ${selected?.id === m.id ? "border-[var(--accent)]" : "border-[var(--ink)]"} group text-left`} data-testid={`media-item-${m.id}`}>
              <div className="aspect-square overflow-hidden bg-[var(--line-soft)] relative">
                {m.media_type === "image" && (m.variants?.thumb || m.variants?.webp) ? (
                  <img src={ASSET_URL(m.variants.thumb || m.variants.webp)} alt={m.alt_de} className="w-full h-full object-cover" />
                ) : m.media_type === "svg" && m.variants?.original ? (
                  <img src={ASSET_URL(m.variants.original)} alt={m.alt_de} className="w-full h-full object-contain p-3 bg-white" />
                ) : m.media_type === "video" && m.variants?.original ? (
                  <>
                    <video src={ASSET_URL(m.variants.original)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    <span className="absolute bottom-1 right-1 bg-[var(--ink)] text-[var(--bg)] rounded-full p-1"><Film size={12} /></span>
                  </>
                ) : m.media_type === "html_animation" && m.variants?.original ? (
                  <>
                    <iframe title={m.original_filename} src={ASSET_URL(m.variants.original)} className="w-full h-full border-0 pointer-events-none" scrolling="no" />
                    <span className="absolute bottom-1 right-1 bg-[var(--ink)] text-[var(--bg)] rounded-full p-1"><Code2 size={12} /></span>
                  </>
                ) : m.media_type === "document" ? (
                  m.variants?.thumb || m.variants?.preview ? (
                    <>
                      <img src={ASSET_URL(m.variants.thumb || m.variants.preview)} alt={m.original_filename} className="w-full h-full object-cover object-top bg-white" />
                      <span className="absolute bottom-1 right-1 bg-[var(--ink)] text-[var(--bg)] rounded-full p-1"><FileText size={12} /></span>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><FileText size={28} /></div>
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-mono text-[11px] uppercase">{m.media_type}</div>
                )}
              </div>
              <div className="p-2 border-t-2 border-[var(--ink)] font-mono text-[10px] truncate">{m.original_filename}</div>
            </button>
            <button type="button" onClick={() => toggleSelected(m.id)} className={`absolute top-2 left-2 w-8 h-8 border-2 border-[var(--ink)] rounded-md font-extrabold shadow-md ${selectedIds.has(m.id) ? "bg-red-600 text-white" : "bg-white text-[var(--ink)]"}`} aria-label={`${m.original_filename} zum Löschen auswählen`} data-testid={`select-media-${m.id}`}>
              {selectedIds.has(m.id) ? "✓" : ""}
            </button>
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); remove(m.id); }}
              disabled={busy}
              className="absolute top-2 right-2 inline-flex h-9 w-9 items-center justify-center rounded-md border-2 border-red-700 bg-red-600 text-white shadow-md hover:bg-red-700 disabled:opacity-40"
              aria-label={`${m.original_filename} dauerhaft löschen`}
              title="Medium dauerhaft löschen"
              data-testid={`delete-media-${m.id}`}
            >
              <Trash2 size={16} />
            </button>
            </div>
          ))}
        </div>
        <aside className="col-span-12 md:col-span-4">
          {selected ? (
            <div className="border-2 border-[var(--ink)] p-4 space-y-4 sticky top-6" data-testid="media-detail">
              {selected.media_type === "image" && (selected.variants?.webp || selected.variants?.original) && (
                <img src={ASSET_URL(selected.variants.webp || selected.variants.original)} alt={selected.alt_de} className="w-full border-2 border-[var(--ink)]" />
              )}
              {selected.media_type === "svg" && selected.variants?.original && (
                <img src={ASSET_URL(selected.variants.original)} alt={selected.alt_de} className="w-full border-2 border-[var(--ink)] bg-white p-6" />
              )}
              {selected.media_type === "video" && selected.variants?.original && (
                <video src={ASSET_URL(selected.variants.original)} controls muted playsInline className="w-full border-2 border-[var(--ink)] bg-black" data-testid="media-video-preview" />
              )}
              {selected.media_type === "html_animation" && selected.variants?.original && (
                <div className="space-y-2">
                  <div className="aspect-video border-2 border-[var(--ink)] overflow-hidden bg-white">
                    <iframe title={selected.original_filename} src={ASSET_URL(selected.variants.original)} className="w-full h-full border-0" data-testid="media-html-preview" />
                  </div>
                  <a href={ASSET_URL(selected.variants.original)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase underline"><ExternalLink size={11}/> In neuem Tab öffnen</a>
                </div>
              )}
              {selected.media_type === "document" && selected.variants?.original && (
                <div className="space-y-2">
                  {(selected.variants?.preview || selected.variants?.thumb) && (
                    <img src={ASSET_URL(selected.variants.preview || selected.variants.thumb)} alt={selected.original_filename} className="w-full border-2 border-[var(--ink)] bg-white" />
                  )}
                  <button onClick={() => setViewingPdf({ src: selected.variants.original, title: selected.original_filename })} className="brut-btn !py-2 !px-3 text-xs" data-testid="media-view-pdf"><Eye size={12} /> Ansehen</button>
                </div>
              )}
              {(selected.width || selected.size_bytes) ? (
                <div className="font-mono text-[10px] uppercase opacity-70">{selected.width ? `${selected.width}×${selected.height} · ` : ""}{selected.size_bytes ? `${(selected.size_bytes/1024).toFixed(0)} KB` : ""}</div>
              ) : null}
              <div>
                <label className="brut-label">Titel</label>
                <input className="brut-input" value={selected.title || ""} onChange={e => setSelected({ ...selected, title: e.target.value })} />
              </div>
              <div>
                <label className="brut-label">Alt-Text DE</label>
                <textarea className="brut-textarea" rows={2} value={selected.alt_de || ""} onChange={e => setSelected({ ...selected, alt_de: e.target.value })} />
              </div>
              <div>
                <label className="brut-label">Alt-Text EN</label>
                <textarea className="brut-textarea" rows={2} value={selected.alt_en || ""} onChange={e => setSelected({ ...selected, alt_en: e.target.value })} />
              </div>
              <div>
                <label className="brut-label">Copyright</label>
                <input className="brut-input" value={selected.copyright || ""} onChange={e => setSelected({ ...selected, copyright: e.target.value })} />
              </div>
              <div className="flex flex-wrap gap-2">
                {selected.media_type === "image" && (
                  <button onClick={() => genAlt(selected.id)} disabled={busy} className="brut-btn !py-2 !px-3 text-xs" data-testid="gen-alt-btn"><Sparkles size={12}/> Alt-Text KI</button>
                )}
                <button onClick={saveMeta} disabled={busy} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs"><Save size={12}/> Speichern</button>
                <button onClick={() => remove(selected.id)} className="border-2 border-red-600 text-red-600 px-3 py-2 font-mono text-xs uppercase ml-auto"><Trash2 size={12}/> Löschen</button>
              </div>
            </div>
          ) : <div className="font-mono text-xs opacity-60 uppercase">Datei auswählen für Details</div>}
        </aside>
      </div>
      {viewingPdf && <PdfViewer src={viewingPdf.src} title={viewingPdf.title} onClose={() => setViewingPdf(null)} />}
    </div>
  );
}
